import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import {
  Container,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatInTimeZone } from "date-fns-tz";
import { faPlus, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { Navigate, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import NotyfContext from "../../contexts/NotyfContext.js";
import axios from "axios";
import Loader from "../../components/Loader.js";
import { confirmAlert } from "react-confirm-alert"; // Import
import { ShowConfirm } from "../../components/Alerts.js";
import useAuth from "../../hooks/useAuth.js";
import useAppDispatch from "../../hooks/useAppDispatch.js";
import useLayout from "../../hooks/useLayout.js";
import { setID } from "../../redux/slices/events.js";
import { canManageEvent } from "../../utils/eventPermissions.js";
import {
  getErrorPaths,
  scrollToFirstError,
  scrollToFirstInvalidTarget,
} from "../../utils/formErrorNavigation.js";
import { jsonCustomizacao, schemaCustomizacao } from "../../components/Json.js";
import {
  GameType,
  ApplicationsList,
  CustomizationsList,
  AppDefinitions,
  AppSideButtonPanel,
} from "../../components/Applications.js";
import { convertDateToDisplay } from "../../assets/functions.js";

const Aplicativos = () => {
  const { tipo, aplicativos_customizacao } = useAuth();
  const canAccessApplications = Number(tipo) === 1 || aplicativos_customizacao === 1;
  const [aplicativos, setAplicativos] = useState(false);
  const [dataAplicativo, setDataAplicativo] = useState(false);

  useEffect(() => {
    if (!canAccessApplications) {
      return;
    }

    axios.get(`/api/application`)
      .then(function (response) {
        var aplicativos = response.data.message
        setAplicativos(aplicativos);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [canAccessApplications]);

  const handleSetDataAplicativo = useCallback((value) => {
    setDataAplicativo(value);
    if (value) {
      window.history.pushState({ type: 'application_view', data: value }, "");
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      if (state && (state.type === 'application_view' || state.appData)) {
        const appData = state.appData || state.data;
        setDataAplicativo((current) => {
          // Avoid re-setting if ID is same, to prevent re-renders/unmounts of Child
          if (current && current.aplicativo_id === appData.aplicativo_id) {
            return current;
          }
          return appData;
        });
      } else {
        setDataAplicativo(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  if (!canAccessApplications) {
    return <Navigate to="/" />;
  }

  if (!aplicativos) {
    return <Loader></Loader>;
  }

  if (!dataAplicativo) {
    return (
      <Container className="p-0">
        <Breadcrumb>
          <Breadcrumb.Item active>Aplicativos</Breadcrumb.Item>
        </Breadcrumb>
        <h6 className="text-muted mb-3">
          Selecione um aplicativo para criar ou editar suas customizações
        </h6>
        <ApplicationsList aplicativos={aplicativos} setDataAplicativo={handleSetDataAplicativo} />
      </Container>
    );
  } else {
    return (
      <Customizations
        dataAplicativo={dataAplicativo}
        setDataAplicativo={handleSetDataAplicativo}
      />
    );
  }
};

export const Customizations = ({ dataAplicativo, setDataAplicativo }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dataTable, setDataTable] = useState(false);
  const userState = useAuth();
  const { user_id } = userState;
  const [fonts, setFonts] = useState()
  const [dataCustomizacao, setDataCustomizacao] = useState("list");
  const [showModalGameType, setShowModalGameType] = useState(false);
  const [updateDataTable, setDataTableUpdate] = useState(false);
  const [customizationScope, setCustomizationScope] = useState("mine");
  const { setLoading } = useLayout();
  const notyf = useContext(NotyfContext);
  const isAdmin = Number(userState?.tipo) === 1;
  const shouldLoadAllCustomizations = isAdmin && customizationScope === "all";

  const handleCustomizationScopeChange = useCallback((scope) => {
    setDataTable(false);
    setCustomizationScope(scope);
  }, []);

  function createCustomization(gameMode = 0) {
    if (dataAplicativo.modes > 0 && gameMode === 0) {
      setShowModalGameType(true);
      return;
    }
    setDataCustomizacao(false);
    let dateX = new Date();
    let dateNow = formatInTimeZone(dateX, "UTC", "yyyy-MM-dd HH:mm");
    let json = jsonCustomizacao(dataAplicativo.aplicativo_id)
    let data = {
      user_id: user_id,
      aplicativo_id: dataAplicativo.aplicativo_id,
      titulo: '',
      json: JSON.stringify(json),
      data_edicao: dateNow,
      version: 0.0
    }
    axios.post("/api/customization", data)
      .then(function (response) {
        data.customizacao_id = response.data.message.insertId
        data.json = JSON.parse(data.json)
        handleSetDataCustomizacao(data);
      })
      .catch(function (error) {
        notyf.open({
          type: "error",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
  }

  function deleteCustomization(customizacao_id) {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <ShowConfirm
            title="Confirmar ação"
            description="Deseja remover essa customização?"
            action={handleDelete}
            onClose={onClose}
            param={customizacao_id}
          />
        );
      },
    });
  }

  function handleDelete(customizacao_id) {
    setLoading(true)
    axios.delete(`/api/customization/${customizacao_id}`)
      .then(function (response) {
        setLoading(false)
        setDataTableUpdate((prevCount) => prevCount + 1);
      })
      .catch(function (error) {
        setLoading(false)
        notyf.open({
          type: "danger",
          message: error.response.data.message,
          ripple: true,
          dismissible: true,
        });
      });
  }

  useEffect(() => {
    if (!fonts) {
      //Get google fonts
      axios.get(`/api/google/fonts`)
        .then((response) => {
          const formattedFonts = response.data.message.items
            .map((item) => ({
              value: item.family,
              label: item.family,
              font: item.family,
            }));
          setFonts(formattedFonts);
        })
        .catch((error) => {
          console.log(error)
          setFonts([]);
          console.log("Erro ao carregar fontes do Google Fonts:", error);
        });
    }
    //Get aplicativo customizations
    const scopeQuery = shouldLoadAllCustomizations ? "&all_users=true" : "";
    axios.get(`/api/customization?aplicativo_id=${dataAplicativo.aplicativo_id}${scopeQuery}`)
      .then(function (response) {
        var data = response.data.message
        data.forEach(function callback(value, index) {
          try {
            value.json = JSON.parse(value.json);
          } catch (error) {
            value.json = {}
          }
          value.data_edicao = convertDateToDisplay(
            value.data_edicao
          );
        });
        setLoading(false)
        setDataTable(data);
      })
      .catch(function (error) {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDataTable, dataAplicativo, shouldLoadAllCustomizations]);

  const handleSetDataCustomizacao = useCallback((value) => {
    setDataCustomizacao(value);
    if (value && value !== "list") {
      // Include appData (dataAplicativo) in the nested state so parent can restore/maintain itself
      window.history.pushState({ type: 'customization_edit', data: value, appData: dataAplicativo }, "");
    }
  }, [dataAplicativo]);

  const handleOpenEvent = useCallback((eventData) => {
    if (!eventData?.evento_id) {
      return;
    }

    const platform = eventData.platform || 1;

    dispatch(setID({
      id: eventData.evento_id,
      platform,
      showHistory: !canManageEvent(userState, platform),
    }));
    navigate("/");
  }, [dispatch, navigate, userState]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.type === 'customization_edit') {
        setDataCustomizacao(event.state.data);
      } else {
        setDataCustomizacao("list");
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  if (!dataTable || !dataCustomizacao) {
    return <Loader></Loader>;
  }

  if (dataCustomizacao === "list") {
    return (
      <>
        <Container className="p-0">
          <Button
            variant="primary"
            className="float-end"
            onClick={() => {
              createCustomization();
            }}
          >
            <FontAwesomeIcon icon={faPlus} size="1x" />
          </Button>
          <Button
            variant="primary"
            className="float-end me-2"
            onClick={() => {
              setDataAplicativo(false);
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Voltar
          </Button>
          <Breadcrumb>
            <Breadcrumb.Item
              onClick={() => {
                setDataAplicativo(false);
              }}>
              Aplicativos
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{dataAplicativo.nome}</Breadcrumb.Item>
          </Breadcrumb>
          <h6 className="text-muted mb-4">{dataAplicativo.descricao}</h6>
          <CustomizationsList
            customizations={dataTable}
            setDataCustomizacao={handleSetDataCustomizacao}
            deleteCustomization={deleteCustomization}
            onOpenEvent={handleOpenEvent}
            isAdmin={isAdmin}
            customizationScope={customizationScope}
            setCustomizationScope={handleCustomizationScopeChange}
          />
        </Container>
        <GameType
          dataAplicativo={dataAplicativo}
          createCustomization={createCustomization}
          showModalGameType={showModalGameType}
          setShowModalGameType={setShowModalGameType}
        />
      </>
    );
  } else {
    return (
      <Edit
        fonts={fonts}
        dataAplicativo={dataAplicativo}
        dataCustomizacao={dataCustomizacao}
        setDataCustomizacao={handleSetDataCustomizacao}
        setDataTableUpdate={setDataTableUpdate}
        setDataAplicativo={setDataAplicativo}
        notyf={notyf}
      />
    );
  }
};

const Edit = ({
  fonts,
  dataAplicativo,
  dataCustomizacao,
  setDataCustomizacao,
  setDataTableUpdate,
  setDataAplicativo,
  notyf,
}) => {
  const { setLoading } = useLayout();
  const applyOnlyRef = useRef(false);
  const setFieldValueRef = useRef(null);

  let schema;
  schema = schemaCustomizacao(dataAplicativo.aplicativo_id);

  function handlePreviewSuccess(filename, setFieldValue, field) {
    setFieldValue(field, filename);
  }

  function handlePreviewError(error) {
    notyf.open({
      type: "danger",
      message: error,
      ripple: true,
      dismissible: true,
    });
  }

  const isApp10MediaConfigModalError = (errorPath) => {
    if (Number(dataAplicativo.aplicativo_id) !== 10) return false;

    return [
      "json.apresentation.configs.videoDuration",
      "json.apresentation.configs.falai",
    ].some(
      (modalPath) =>
        errorPath === modalPath ||
        errorPath.startsWith(`${modalPath}.`) ||
        errorPath.startsWith(`${modalPath}[`),
    );
  };

  const waitForDomUpdate = () =>
    new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
    });

  const scrollToValidationError = (errorPaths) =>
    scrollToFirstError(errorPaths) || scrollToFirstInvalidTarget();

  const navigateToValidationError = async (validationErrors) => {
    const errorPaths = getErrorPaths(validationErrors);
    if (errorPaths.length === 0) return false;

    const hasApp10MediaConfigModalError = errorPaths.some(
      isApp10MediaConfigModalError,
    );

    if (hasApp10MediaConfigModalError) {
      document.dispatchEvent(
        new CustomEvent("app10:open-media-config-modal", {
          detail: { errorPaths },
        }),
      );
      await waitForDomUpdate();
    }

    if (scrollToValidationError(errorPaths)) return true;

    const retryScroll = (remainingAttempts) => {
      window.setTimeout(() => {
        if (scrollToValidationError(errorPaths)) return;

        if (remainingAttempts > 1) {
          retryScroll(remainingAttempts - 1);
          return;
        }

        notyf.open({
          type: "error",
          message: hasApp10MediaConfigModalError
            ? "A configuração de mídia foi aberta. Corrija os campos destacados antes de salvar."
            : "Não foi possível localizar o campo com erro. Corrija os campos destacados antes de salvar.",
          ripple: true,
          dismissible: true,
        });
      }, 150);
    };

    retryScroll(4);
    return true;
  };

  const handleCustomSubmit = async (applyOnly, values, {
    validateForm,
    handleSubmit,
    setFieldValue,
  }) => {
    applyOnlyRef.current = applyOnly;
    setFieldValueRef.current = setFieldValue;

    const validationErrors = await validateForm();
    if (await navigateToValidationError(validationErrors)) return;

    handleSubmit();
  };

  function updateCustomization(values, actions) {
    const applyOnly = applyOnlyRef.current;
    applyOnlyRef.current = false;
    let dateX = new Date();
    let dateNow = formatInTimeZone(dateX, "UTC", "yyyy-MM-dd HH:mm");
    setLoading(true)
    axios.put(`/api/customization/${values.customizacao_id}`, {
      titulo: values.titulo,
      json: values.json,
      data_edicao: dateNow,
      version: parseFloat(values.version) + 0.1
    })
      .then(function (response) {
        if (response.status === 200) {
          setLoading(false)
          const newVersion = parseFloat(values.version) + 0.1;
          const hasLinkedEventsToUpdate = Boolean(response.data.elementsToUpdate);
          const handleOnClose = (onClose = undefined) => {
            if (!applyOnly) {
              setDataCustomizacao("list");
              setLoading(true)
              setDataTableUpdate((prevCount) => prevCount + 1);
            } else {
              // Update Formik's version field so the next save increments from the correct base
              if (setFieldValueRef.current) {
                setFieldValueRef.current('version', newVersion);
              }
              actions.setSubmitting(false);
            }
            if (onClose) {
              onClose();
            }
            notyf.open({
              type: "success",
              message: "Customização salva com sucesso",
              ripple: true,
              dismissible: true,
            });
          };

          if (hasLinkedEventsToUpdate) {
            confirmAlert({
              closeOnEscape: false,
              closeOnClickOutside: false,
              customUI: ({ onClose }) => {
                return (
                  <ShowConfirm
                    title="Confirmar ação"
                    description="Deseja atualizar todos os eventos que usam esta customização? Você também pode atualizar manualmente ao editar o evento"
                    action={updateCustomizationVersion}
                    onClose={(e) => handleOnClose(onClose)}
                    param={{ customizacao_id: values.customizacao_id }}
                    textConfirm={'Sim, atualizar todos os eventos'}
                    textCancel={'Não'}
                  />
                );
              },
            });
          } else {
            handleOnClose(undefined)
          }
        } else {
          setLoading(false)
          actions.setSubmitting(false);
          notyf.open({
            type: "error",
            message: "Houve um erro, tente novamente",
            ripple: true,
            dismissible: true,
          });
        }
      })
      .catch(function (error) {
        setLoading(false)
        actions.setSubmitting(false);
        notyf.open({
          type: "error",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
  }

  function updateCustomizationVersion({ customizacao_id }) {
    setLoading(true)
    axios.put(`/api/event/customizationVersion/${customizacao_id}`)
      .then(function (response) {
        setLoading(false)
        notyf.open({
          type: "success",
          message: "Eventos com essa customização foram atualizados",
          ripple: true,
          dismissible: true,
        });
      })
      .catch(function (error) {
        setLoading(false)
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
  }

  return (
    <React.Fragment>
      <Container className="p-0">
        <Formik
          validationSchema={schema}
          onSubmit={updateCustomization}
          enableReinitialize
          initialValues={{
            customizacao_id: dataCustomizacao.customizacao_id,
            user_id: dataCustomizacao.user_id,
            aplicativo_id: dataCustomizacao.aplicativo_id,
            titulo: dataCustomizacao.titulo,
            json: dataCustomizacao.json,
            data_edicao: dataCustomizacao.data_edicao,
            version: dataCustomizacao.version
          }}
        >
          {({
            handleSubmit,
            handleChange,
            validateForm,
            values,
            touched,
            isValid,
            errors,
            dirty,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form noValidate onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }} onSubmit={async (e) => {
              e.preventDefault();
              await handleCustomSubmit(false, values, { validateForm, handleSubmit, setFieldValue });
            }}>
              <AppSideButtonPanel
                isSubmitting={isSubmitting}
                onSubmit={(applyOnly) => handleCustomSubmit(applyOnly, values, { validateForm, handleSubmit, setFieldValue })}
                onReset={() => {
                  confirmAlert({
                    closeOnEscape: false,
                    closeOnClickOutside: false,
                    customUI: ({ onClose }) => (
                      <ShowConfirm
                        title="Confirmar ação"
                        description="Deseja restaurar todas as configurações para os valores padrão?"
                        action={() => {
                          setFieldValue('json', jsonCustomizacao(dataAplicativo.aplicativo_id));
                          notyf.open({
                            type: "success",
                            message: "Customização restaurada para os valores padrão",
                            ripple: true,
                            dismissible: true,
                          });
                        }}
                        onClose={onClose}
                      />
                    ),
                  });
                }}
              />
              <div className="mb-4">
                <Button
                  variant="primary"
                  className="float-end me-2"
                  onClick={() => {
                    setDataCustomizacao("list");
                    setLoading(true)
                    setDataTableUpdate((prevCount) => prevCount + 1);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Voltar
                </Button>
                <Breadcrumb>
                  <Breadcrumb.Item
                    onClick={() => {
                      setDataAplicativo(false);
                    }}
                  >
                    Aplicativos
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    onClick={() => {
                      setDataCustomizacao("list");
                      setDataTableUpdate((prevCount) => prevCount + 1);
                      setLoading(true)
                    }}
                  >
                    {dataAplicativo.nome}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>
                    {values.titulo ? "Editar customização" : "Nova customização"}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </div>
              <AppDefinitions fonts={fonts} aplicativo_id={dataAplicativo.aplicativo_id} handleChange={handleChange} values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handlePreviewSuccess={handlePreviewSuccess} handlePreviewError={handlePreviewError}></AppDefinitions>
            </Form>
          )}
        </Formik>
      </Container>
    </React.Fragment>
  );
};

export default Aplicativos;
